// DERIVADO DE: gestion-web-cliente@1a5ce2d39
//   los estados están repartidos entre `listado-servicio-tecnico`,
//   `crear-editar-evento-tecnico-generico` y `tratamiento-generico`.
// CAMBIOS: un solo lugar, con qué significa cada estado y qué se puede hacer
//   desde ahí.

import type {
  EstadoEventoTecnico,
  EstadoPedidoMaterial,
  IEventoGenerico,
  IMaterialServicioTecnico,
  IPermiso,
  IUsuario,
  IValoresEventoTecnico,
  RangoHorario,
  TipoMaterialServicioTecnico,
} from 'modelos';

/**
 * El tono de un badge. Son los del manual de marca (ok, aviso —`alerta`, el
 * relleno—, error) más los
 * dos neutros de FARO: cyan para lo que está en curso y gris para lo que ya no
 * pide nada. Se usa como sufijo de `.etiqueta--`.
 */
export type TonoBadge = 'ok' | 'alerta' | 'error' | 'info' | 'neutro';

export interface PresentacionEstado {
  readonly etiqueta: string;
  readonly icono: string;
  /** Qué pasó, en una línea. Va al tooltip. */
  readonly explicacion: string;
  /** Token de color del tema. El color nunca va solo: siempre con ícono. */
  readonly color: string;
  /** El badge del listado. Sin él, se deduce del color. */
  readonly tono?: TonoBadge;
}

/**
 * El recorrido de una solicitud.
 *
 * La **etiqueta es el estado tal como se guarda** (el mismo nombre que en IRIX,
 * en los filtros y en la base): otro nombre parece otro estado. Lo que significa
 * va en `explicacion`, que es el tooltip.
 *
 * No es un semáforo: cada estado dice **quién tiene la pelota**. Pendiente y
 * Asignado son de administración; En Atención y Pendiente de Aprobación son del
 * técnico; Finalizado la cierra administración, y es el único momento en que se
 * aplica un cambio de nodo propuesto.
 */
export const ESTADOS: Record<EstadoEventoTecnico, PresentacionEstado> = {
  Pendiente: {
    etiqueta: 'Pendiente',
    icono: 'schedule',
    explicacion: 'Nadie la tomó todavía: no tiene técnico asignado.',
    color: 'var(--ui-aviso)',
    tono: 'alerta',
  },
  Asignado: {
    etiqueta: 'Asignado',
    icono: 'person',
    explicacion: 'Tiene técnico. Le llegó al teléfono y todavía no la abrió.',
    color: 'var(--ui-info)',
    tono: 'info',
  },
  'En Atención': {
    etiqueta: 'En Atención',
    icono: 'build',
    explicacion: 'El técnico está trabajando en ella.',
    color: 'var(--ui-info)',
    tono: 'info',
  },
  'Pendiente de Aprobación': {
    etiqueta: 'Pendiente de Aprobación',
    icono: 'approval',
    explicacion: 'El técnico terminó y administración tiene que revisarla.',
    color: 'var(--ui-aviso)',
    tono: 'alerta',
  },
  'Firma Cliente': {
    etiqueta: 'Firma Cliente',
    icono: 'signature',
    explicacion: 'Falta que el cliente firme la conformidad.',
    color: 'var(--ui-aviso)',
    tono: 'alerta',
  },
  Finalizado: {
    etiqueta: 'Finalizado',
    icono: 'check_circle',
    explicacion: 'Cerrada. Si había un cambio de nodo propuesto, acá se aplicó.',
    color: 'var(--ui-ok)',
    tono: 'ok',
  },
};

/** El orden en que suceden. Sirve para no ofrecer marcha atrás. */
export const ORDEN_ESTADOS: readonly EstadoEventoTecnico[] = [
  'Pendiente',
  'Asignado',
  'En Atención',
  'Pendiente de Aprobación',
  'Firma Cliente',
  'Finalizado',
];

/**
 * Un estado que no está en la lista igual tiene que pintar algo.
 *
 * El backend puede sumar estados antes que el front, y una celda en blanco
 * parece un error de datos cuando en realidad es un estado que no conocemos.
 */
export function presentacionDe(estado: EstadoEventoTecnico | undefined): PresentacionEstado {
  if (!estado) return ESTADOS.Pendiente;
  return (
    ESTADOS[estado] ?? {
      etiqueta: estado,
      icono: 'question_mark',
      explicacion: 'Un estado que esta versión no conoce.',
      color: 'var(--texto-secundario)',
      tono: 'neutro',
    }
  );
}

/**
 * A qué estados se puede pasar desde acá.
 *
 * **No es una máquina de estados estricta y no debería serlo**: el técnico puede
 * dejar de atender una solicitud y devolverla, y administración puede reabrir lo
 * que cerró mal. Lo único que no se ofrece es ir hacia atrás desde Finalizado,
 * porque un cambio de nodo aplicado no se desaplica.
 */
export function siguientes(estado: EstadoEventoTecnico | undefined): EstadoEventoTecnico[] {
  switch (estado) {
    case 'Finalizado':
      return [];
    case 'Pendiente de Aprobación':
    case 'Firma Cliente':
      return ['En Atención', 'Finalizado'];
    case 'En Atención':
      return ['Pendiente de Aprobación', 'Finalizado'];
    default:
      return ['En Atención', 'Pendiente de Aprobación', 'Finalizado'];
  }
}

export const PRIORIDADES = ['Normal', 'Alta', 'Urgente'] as const;
export type Prioridad = (typeof PRIORIDADES)[number];

export function colorPrioridad(prioridad: string | undefined): string | undefined {
  switch (prioridad) {
    case 'Urgente':
      return 'var(--ui-error)';
    case 'Alta':
      return 'var(--ui-aviso)';
    default:
      return undefined;
  }
}

/** El título que habilita el cambio de nodo. Es un dato del cliente, no código. */
export const TITULO_CAMBIO_DE_NODO = 'Cambio de nodo';

export function valoresDe(solicitud: IEventoGenerico): IValoresEventoTecnico {
  return (solicitud.valores ?? {}) as IValoresEventoTecnico;
}

/** El nombre del técnico asignado, o por qué no hay ninguno. */
export function tecnicoDe(solicitud: IEventoGenerico): string {
  const tecnico = solicitud.detallesTecnicos?.tecnico;
  if (!tecnico) return 'Sin asignar';
  return tecnico.datosPersonales?.nombre || tecnico.usuario || 'Sin asignar';
}

/**
 * Un cambio de nodo propuesto y todavía sin aplicar.
 *
 * Es lo único de la solicitud que cambia el inventario, y no lo aplica el
 * técnico: queda acá esperando que administración finalice.
 */
export function cambioPendiente(solicitud: IEventoGenerico): boolean {
  const cambio = solicitud.detallesTecnicos?.cambioEntidadPropuesto;
  return !!cambio?.idEntidadNueva && !cambio.aplicado;
}

// ── Listado ───────────────────────────────────────────────────────────────────

/** El badge de la prioridad. `Normal` no llama la atención: es gris. */
export function tonoPrioridad(prioridad: string | undefined): TonoBadge {
  switch (prioridad) {
    case 'Urgente':
      return 'error';
    case 'Alta':
      return 'alerta';
    default:
      return 'neutro';
  }
}

/** Lo que se pidió: arreglar algo o ir a mirar. */
export const TIPOS_SOLICITUD = [
  { valor: 'Servicio Técnico', etiqueta: 'Servicio Técnico', icono: 'construction' },
  { valor: 'Verificación', etiqueta: 'Verificación', icono: 'find_in_page' },
] as const;

export function tipoDe(solicitud: IEventoGenerico): (typeof TIPOS_SOLICITUD)[number] {
  return solicitud.filtrador === 'Verificación' ? TIPOS_SOLICITUD[1] : TIPOS_SOLICITUD[0];
}

/** El código que ve el vecino. El backend lo genera al crear y va en mayúsculas. */
export function codigoDe(solicitud: IEventoGenerico | undefined): string | undefined {
  if (!solicitud) return undefined;
  return valoresDe(solicitud).numeroIncidente?.toUpperCase() || undefined;
}

// ── Técnicos ──────────────────────────────────────────────────────────────────

/** Un técnico tal como se elige: el usuario, su foto y el permiso que lo hace técnico. */
export interface OpcionTecnico {
  readonly id: string;
  readonly nombre: string;
  /** "Nombre (usuario)", como lo busca IRIX. */
  readonly etiqueta: string;
  readonly usuario?: string;
  readonly foto?: string;
  readonly idCliente?: string;
  readonly cliente?: string;
}

export function nombreDeUsuario(usuario: IUsuario | undefined): string | undefined {
  return usuario?.datosPersonales?.nombre?.trim() || usuario?.usuario || undefined;
}

/**
 * Los técnicos que atienden alumbrado.
 *
 * Técnico es un permiso con la función Técnico, y además tiene especialidades
 * (los módulos del permiso). El de alarmas no sabe de luminarias: IRIX lo filtra
 * con `tecnicoAtiendeTipo`, y acá es el mismo criterio para el único tipo que hay.
 * Un permiso sin módulos cargados no se descarta: es cómo quedaron los viejos.
 */
export function opcionesTecnico(permisos: readonly IPermiso[]): OpcionTecnico[] {
  return permisos
    .filter((p) => {
      const modulos = p.modulos as Record<string, boolean> | undefined;
      return !modulos || modulos['moduloLuminarias'] === true;
    })
    .map((p) => {
      const u = p.usuario;
      const nombre = nombreDeUsuario(u) ?? p.nombreUsuario ?? 'Sin nombre';
      const conNombre = u?.datosPersonales?.nombre?.trim();
      return {
        id: u?._id ?? '',
        nombre,
        etiqueta: conNombre && u?.usuario ? `${conNombre} (${u.usuario})` : nombre,
        usuario: u?.usuario,
        foto: u?.datosPersonales?.foto,
        idCliente: p.idCliente,
        cliente: p.cliente?.nombre,
      };
    })
    .filter((t) => t.id)
    .sort((a, b) => a.etiqueta.localeCompare(b.etiqueta, 'es', { sensitivity: 'base' }));
}

// ── Disponibilidad horaria ────────────────────────────────────────────────────

export const DIAS = [
  'Lunes',
  'Martes',
  'Miércoles',
  'Jueves',
  'Viernes',
  'Sábado',
  'Domingo',
] as const;

/** "Lunes a viernes", "Sábado y domingo", "Todos los días" o la lista. */
export function textoDias(dias: readonly string[] | undefined): string {
  if (!dias?.length || dias.length === DIAS.length) return 'Todos los días';
  const orden = DIAS.filter((d) => dias.includes(d));
  const habiles = DIAS.slice(0, 5);
  if (orden.length === 5 && habiles.every((d) => orden.includes(d))) return 'Lunes a viernes';
  if (orden.length === 2 && orden.includes('Sábado') && orden.includes('Domingo')) {
    return 'Sábado y domingo';
  }
  if (orden.length === 1) return orden[0];
  return `${orden.slice(0, -1).join(', ')} y ${orden[orden.length - 1]}`;
}

export function textoRango(rango: RangoHorario): string {
  return `${textoDias(rango.dias)} · ${rango.horarioDesde || '00:00'} a ${rango.horarioHasta || '23:59'}`;
}

// ── Materiales ────────────────────────────────────────────────────────────────

export interface PresentacionMaterial {
  readonly etiqueta: string;
  readonly icono: string;
  readonly tono: TonoBadge;
  readonly explicacion: string;
}

export const TIPOS_MATERIAL: Record<TipoMaterialServicioTecnico, PresentacionMaterial> = {
  pedido: {
    etiqueta: 'Pedido',
    icono: 'shopping_cart',
    tono: 'info',
    explicacion: 'El técnico lo pidió desde la calle para terminar el trabajo.',
  },
  usado: {
    etiqueta: 'Usado',
    icono: 'handyman',
    tono: 'neutro',
    explicacion: 'Lo que se puso en esa visita.',
  },
  asignado: {
    etiqueta: 'Asignado',
    icono: 'inventory_2',
    tono: 'alerta',
    explicacion: 'Lo que el técnico lleva en la caja: no cuelga de ninguna solicitud.',
  },
};

export const ESTADOS_MATERIAL: Record<EstadoPedidoMaterial, PresentacionMaterial> = {
  Pendiente: {
    etiqueta: 'Pendiente',
    icono: 'hourglass_top',
    tono: 'alerta',
    explicacion: 'Espera que alguien lo apruebe.',
  },
  Aprobado: {
    etiqueta: 'Aprobado',
    icono: 'thumb_up',
    tono: 'info',
    explicacion: 'Aprobado: el técnico vuelve con el material a terminar.',
  },
  Usado: {
    etiqueta: 'Usado',
    icono: 'check_circle',
    tono: 'ok',
    explicacion: 'Quedó puesto.',
  },
  Anulado: {
    etiqueta: 'Anulado',
    icono: 'cancel',
    tono: 'error',
    explicacion: 'No se va a entregar.',
  },
  Asignado: {
    etiqueta: 'Asignado',
    icono: 'inventory_2',
    tono: 'ok',
    explicacion: 'Está en la caja del técnico.',
  },
  Devuelto: {
    etiqueta: 'Devuelto',
    icono: 'undo',
    tono: 'neutro',
    explicacion: 'Salió de la caja del técnico.',
  },
};

export const ORDEN_TIPOS_MATERIAL: readonly TipoMaterialServicioTecnico[] = [
  'pedido',
  'usado',
  'asignado',
];

export const ORDEN_ESTADOS_MATERIAL: readonly EstadoPedidoMaterial[] = [
  'Pendiente',
  'Aprobado',
  'Usado',
  'Anulado',
  'Asignado',
  'Devuelto',
];

export function presentacionTipoMaterial(
  tipo: TipoMaterialServicioTecnico | undefined,
): PresentacionMaterial {
  return (tipo && TIPOS_MATERIAL[tipo]) || TIPOS_MATERIAL.usado;
}

export function presentacionEstadoMaterial(
  estado: EstadoPedidoMaterial | undefined,
): PresentacionMaterial | undefined {
  return estado ? ESTADOS_MATERIAL[estado] : undefined;
}

/** Lo que se ve del material: la descripción, o el id del equipo si no hay. */
export function nombreMaterial(material: IMaterialServicioTecnico): string | undefined {
  return material.descripcion || material.idEntidad || undefined;
}
